import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { fmtDate } from "../lib/labels";
import { PageHeader } from "../components/Layout";
import { ConfirmDialog, EmptyRow } from "../components/Common";
import { RecipeDialog } from "../components/RecipeDialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const TH = "px-4 py-3";
const names = (ids, list, key, all) => (ids.length === 0 ? <span className="text-slate-500">{all}</span> : ids.map((id) => list?.find((x) => x.id === id)?.[key] || "?").join(", "));

const RecipeRow = ({ r, orgs, zones, onEdit, onDelete, onToggle }) => (
  <tr className="row-hover border-b border-slate-800/70 hover:bg-slate-900/60" data-testid={`recipe-row-${r.name}`}>
    <td className={TH}><div className="font-medium">{r.label}</div><div className="font-mono text-[11px] text-slate-500">{r.name}</div></td>
    <td className={`${TH} font-mono text-xs text-emerald-300`}>{r.result_amount}× {r.result_item}</td>
    <td className={`${TH} font-mono text-xs text-slate-400`} data-testid={`recipe-materials-${r.name}`}>{r.materials.map((m) => `${m.amount}× ${m.item}`).join(" + ")}</td>
    <td className={`${TH} font-mono text-xs text-slate-400`}>{r.craft_time_seconds} s</td>
    <td className={`${TH} text-xs`} data-testid={`recipe-orgs-${r.name}`}>{names(r.allowed_org_ids, orgs, "label", "Todas")}</td>
    <td className={`${TH} text-xs`} data-testid={`recipe-zones-${r.name}`}>{names(r.zone_ids, zones, "name", "Todas")}</td>
    <td className={TH}><Switch checked={r.active} onCheckedChange={() => onToggle(r)} data-testid={`recipe-active-toggle-${r.name}`} /></td>
    <td className={TH}>
      <div className="flex justify-end gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(r)} data-testid={`recipe-edit-${r.name}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onDelete(r)} data-testid={`recipe-delete-${r.name}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
      </div>
    </td>
  </tr>
);

const CraftLog = () => {
  const { data: logs } = useApi("/admin/craft-logs?limit=100", 15000);
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full text-left" data-testid="craft-log-table">
        <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          <tr><th className={TH}>Data</th><th className={TH}>Organização</th><th className={TH}>CitizenID</th><th className={TH}>Receita</th><th className={`${TH} text-right`}>Qtd.</th></tr>
        </thead>
        <tbody>
          {!logs && <EmptyRow cols={5} text="A carregar…" testid="craft-log-loading" />}
          {logs && logs.length === 0 && <EmptyRow cols={5} text="Ainda não há crafts registados." testid="craft-log-empty" />}
          {(logs || []).map((l) => (
            <tr key={l.id} className="border-b border-slate-800/70" data-testid={`craft-log-row-${l.id}`}>
              <td className={`${TH} font-mono text-xs text-slate-400`}>{fmtDate(l.created_at)}</td>
              <td className={`${TH} text-sm`}>{l.gang}</td>
              <td className={`${TH} font-mono text-xs`}>{l.citizenid}</td>
              <td className={`${TH} text-sm`}>{l.recipe}</td>
              <td className={`${TH} text-right font-mono`}>{l.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function RecipesPage() {
  const { data: recipes, reload } = useApi("/admin/recipes");
  const { data: orgs } = useApi("/admin/orgs");
  const { data: zones } = useApi("/admin/zones");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (r) => {
    const { id, created_at, ...body } = r;
    run(() => api.put(`/admin/recipes/${id}`, { ...body, active: !r.active }), r.active ? "Receita desativada." : "Receita ativada.");
  };

  return (
    <div>
      <PageHeader eyebrow="Mesas de craft" title="Receitas">
        <Button onClick={() => setEditing({})} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="create-recipe-button"><Plus className="mr-1 h-4 w-4" />Nova receita</Button>
      </PageHeader>
      <Tabs defaultValue="recipes" className="fade-up">
        <TabsList className="mb-4 border border-slate-800 bg-slate-950">
          <TabsTrigger value="recipes" data-testid="recipes-tab">Receitas</TabsTrigger>
          <TabsTrigger value="log" data-testid="craft-log-tab">Registo de crafts</TabsTrigger>
        </TabsList>
        <TabsContent value="recipes">
          <div className="panel overflow-x-auto">
            <table className="w-full text-left" data-testid="recipes-table">
              <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
                <tr><th className={TH}>Receita</th><th className={TH}>Resultado</th><th className={TH}>Materiais</th><th className={TH}>Tempo</th><th className={TH}>Organizações</th><th className={TH}>Zonas</th><th className={TH}>Ativa</th><th className={TH} /></tr>
              </thead>
              <tbody>
                {recipes && recipes.length === 0 && <EmptyRow cols={8} text="Sem receitas." testid="recipes-empty" />}
                {(recipes || []).map((r) => <RecipeRow key={r.id} r={r} orgs={orgs} zones={zones} onEdit={setEditing} onDelete={setDeleting} onToggle={toggle} />)}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="log"><CraftLog /></TabsContent>
      </Tabs>
      <RecipeDialog recipe={editing} orgs={orgs} zones={zones} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} testid="recipe-delete-dialog"
        title="Eliminar receita?" confirmLabel="Eliminar" description={`A receita ${deleting?.label || ""} será eliminada permanentemente.`}
        onConfirm={() => { run(() => api.delete(`/admin/recipes/${deleting.id}`), "Receita eliminada."); setDeleting(null); }} />
    </div>
  );
}
